/**
 * Create / edit page for a custom firewall rule, modelled on the Owl "Add rule"
 * builder. Reached from the list via `firewall/new?domain=` and
 * `firewall/edit?domain=&id=`.
 *
 * The condition builder is driven by the rule-builder schema
 * (`GET /waf/firewall/schema`): the Field / Operator dropdowns and their value
 * inputs come from the vendor catalog, so the form always matches the live
 * vocabulary. The action sub-forms follow the canonical contract enforced by the
 * backend (block | pass | rate_limit | block_redirect | challenge).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollText, ArrowLeft, Trash2, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafFirewallSchema,
  useWafFirewallRules,
  useCreateFirewallRule,
  useUpdateFirewallRule,
  wafExtractError,
  type FirewallRule,
  type FirewallRuleAction,
  type FirewallRuleCondition,
  type FirewallRuleInput,
  type FirewallField,
  type FirewallOperator,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { geoCountries, humanizeGeoName, acGeoCode } from '../_shared/geo';
import styles from './WafFirewallRules.module.css';

/** Country options for geo operators (value = "{continent}-{country}-00-00-00"). */
const GEO_OPTIONS: MultiSelectOption[] = geoCountries
  .map((c) => ({ value: acGeoCode(c), label: humanizeGeoName(c.name) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/* The action `value` in the schema is the vendor token; the create body wants
   the canonical `type`. Only two differ from a plain copy. */
const VENDOR_TO_CANON: Record<string, string> = { rateLimit: 'rate_limit', blockRedirect: 'block_redirect' };
const canonAction = (v: string) => VENDOR_TO_CANON[v] ?? v;

/** Action segment fallback when the schema can't be loaded. */
const ACTION_FALLBACK = [
  { type: 'block', label: 'Block' },
  { type: 'pass', label: 'Pass' },
  { type: 'rate_limit', label: 'Rate-Limit' },
  { type: 'block_redirect', label: 'Block & Redirect' },
  { type: 'challenge', label: 'Challenge' },
];

const REDIRECT_CODES = [301, 302, 303, 307, 308];
const CHALLENGE_MODES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'javascript-nodelay', label: 'JavaScript (no delay)' },
  { value: 'human', label: 'Human' },
];
const CHALLENGE_TTLS = [300, 600, 900, 1800, 2700, 3600, 7200, 10800, 14400, 28800, 57600];
const fmtTtl = (s: number) => (s % 3600 === 0 ? `${s / 3600} h` : `${s / 60} min`);

/** A redirect URL's host must have 2–6 levels (labels). */
const REDIRECT_HOST_MIN = 2;
const REDIRECT_HOST_MAX = 6;

/** Number of dot-separated labels in a URL's host, or 0 if the URL/host can't be parsed. */
function urlHostLevel(raw: string): number {
  const v = raw.trim();
  if (!v) return 0;
  try {
    const host = new URL(v).hostname;
    return host ? host.split('.').filter(Boolean).length : 0;
  } catch {
    return 0;
  }
}

/** The "Remote address" / "Real client IP" fields expect an IP value, so it's format-checked. */
const fieldIsIp = (f?: FirewallField) => !!f && /remote|client|ip[\s_-]?addr/i.test(`${f.value} ${f.label}`);

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** A plain IPv4 address, optionally with a CIDR prefix (/0–32). */
function isIpAddress(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  const slash = s.indexOf('/');
  if (slash === -1) return IPV4.test(s);
  const addr = s.slice(0, slash);
  const prefix = s.slice(slash + 1);
  return IPV4.test(addr) && /^\d{1,2}$/.test(prefix) && Number(prefix) <= 32;
}

/**
 * Free-form list operators (multi-value, no fixed options) — mirror the SDK's
 * `fwListOps`. Their value is a typed list of strings stored under `values`.
 */
const LIST_OPERATORS = new Set(['pm', '!pm', 'isIn', '!isIn']);
const opIsList = (op?: FirewallOperator) => !!op && LIST_OPERATORS.has(op.value);

/** One condition row in the builder. `logic` connects to the NEXT row. */
interface Row {
  field: string;
  key: string;
  operator: string;
  value: string;
  values: string[];
  logic: string;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? <span className={styles.subHint}>{hint}</span> : null}
    </div>
  );
}

export function WafFirewallRuleForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const id = params.get('id');
  const isEdit = !!id;
  const back = () => navigate(`/services/waf/${serviceId}/firewall`);

  const schemaQ = useWafFirewallSchema(serviceId);
  const fields = schemaQ.data?.fields ?? [];
  const actions = useMemo(
    () =>
      (schemaQ.data?.actions?.length
        ? schemaQ.data.actions.map((a) => ({ type: canonAction(a.value), label: a.label }))
        : ACTION_FALLBACK),
    [schemaQ.data],
  );

  const rulesQ = useWafFirewallRules(serviceId, isEdit ? domain : undefined);
  const rule = useMemo<FirewallRule | undefined>(
    () => (isEdit ? (rulesQ.data ?? []).find((r) => r.id === id) : undefined),
    [isEdit, rulesQ.data, id],
  );

  const fieldFor = (f: string): FirewallField | undefined => fields.find((x) => x.value === f);
  const opFor = (r: Row): FirewallOperator | undefined => fieldFor(r.field)?.operators.find((o) => o.value === r.operator);

  const blankRow = (): Row => {
    const f0 = fields[0];
    const o0 = f0?.operators[0];
    return { field: f0?.value ?? '', key: '', operator: o0?.value ?? '', value: '', values: [], logic: 'and' };
  };

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [actionType, setActionType] = useState('block');
  const [code, setCode] = useState('302');
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState('javascript');
  const [ttl, setTtl] = useState('900');
  const [totalCount, setTotalCount] = useState('');
  const [blockTime, setBlockTime] = useState('60');
  const [rlChallenge, setRlChallenge] = useState(false);
  // Validation hints stay hidden until the first submit attempt (no red on a pristine form).
  const [submitted, setSubmitted] = useState(false);

  // Seed once the schema (and, on edit, the rule) is ready.
  useEffect(() => {
    if (seeded) return;
    if (schemaQ.isLoading || fields.length === 0) return;
    if (isEdit) {
      if (rulesQ.isLoading) return;
      if (rule) seedFrom(rule);
      setSeeded(true);
    } else {
      setRows([blankRow()]);
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, schemaQ.isLoading, fields.length, isEdit, rule, rulesQ.isLoading]);

  function seedFrom(r: FirewallRule) {
    setName(r.name);
    const seededRows: Row[] = (r.conditions ?? []).map((c) => ({
      field: c.field,
      key: c.key ?? '',
      operator: c.operator,
      value: c.value ?? '',
      values: c.values ?? [],
      logic: c.logic || 'and',
    }));
    setRows(seededRows.length ? seededRows : [blankRow()]);
    const a = r.action;
    setActionType(a.type || 'block');
    if (a.code) setCode(String(a.code));
    if (a.url) setUrl(a.url);
    if (a.mode) setMode(a.mode);
    if (a.ttl) setTtl(String(a.ttl));
    if (a.total_count != null) setTotalCount(String(a.total_count));
    if (a.block_time != null) setBlockTime(String(a.block_time));
    if (a.type === 'rate_limit' && a.rate_limit_mode === 'challenge') {
      setRlChallenge(true);
      if (a.rate_limit_challenge_mode) setMode(a.rate_limit_challenge_mode);
      if (a.rate_limit_challenge_ttl) setTtl(String(a.rate_limit_challenge_ttl));
    }
  }

  // ---- row mutations ----
  const patchRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changeField = (i: number, field: string) => {
    const op0 = fieldFor(field)?.operators[0]?.value ?? '';
    patchRow(i, { field, operator: op0, key: '', value: '', values: [] });
  };
  const changeOperator = (i: number, operator: string) => patchRow(i, { operator, value: '', values: [] });

  const addRow = (logic: string) => {
    if (rows.length >= 5) return;
    setRows((rs) => {
      const next = rs.map((r, idx) => (idx === rs.length - 1 ? { ...r, logic } : r));
      return [...next, blankRow()];
    });
  };
  const removeRow = (i: number) => setRows((rs) => (rs.length <= 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const setLogic = (rowIdx: number, logic: string) => patchRow(rowIdx - 1, { logic });

  // ---- build + validate ----
  // What a row requires is derived from its field + operator.
  const opHasOptions = (op?: FirewallOperator) => op?.type === 'geo' || !!op?.options?.length || opIsList(op);
  const keyMissing = (r: Row) => !!fieldFor(r.field)?.input_value && !r.key.trim();
  const valueMissing = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false) return false; // operator takes no value (e.g. exists)
    return opHasOptions(op) ? r.values.length === 0 : !r.value.trim();
  };
  // "Remote address" fields must hold a valid IPv4 (or IPv4/CIDR) value.
  const valueBadFormat = (r: Row) => {
    const op = opFor(r);
    if (!op || op.input_value === false || opHasOptions(op)) return false;
    if (!fieldIsIp(fieldFor(r.field)) || !r.value.trim()) return false;
    return !isIpAddress(r.value);
  };
  const rowComplete = (r: Row) =>
    !!r.field && !!r.operator && !keyMissing(r) && !valueMissing(r) && !valueBadFormat(r);

  const validRows = rows.filter((r) => r.field && r.operator);

  function buildConditions(): FirewallRuleCondition[] {
    return validRows.map((r, idx) => {
      const op = opFor(r);
      const hasOptions = opHasOptions(op);
      const cond: FirewallRuleCondition = { field: r.field, operator: r.operator };
      if (fieldFor(r.field)?.input_value && r.key.trim()) cond.key = r.key.trim();
      if (op?.input_value !== false) {
        if (hasOptions) cond.values = r.values;
        else cond.value = r.value.trim();
      }
      if (idx < validRows.length - 1) cond.logic = r.logic || 'and';
      return cond;
    });
  }

  function buildAction(): FirewallRuleAction {
    switch (actionType) {
      case 'block_redirect':
        return { type: 'block_redirect', code: Number(code) || 302, url: url.trim() };
      case 'challenge':
        return { type: 'challenge', mode, ttl: Number(ttl) || 900 };
      case 'rate_limit': {
        const a: FirewallRuleAction = { type: 'rate_limit', total_count: Number(totalCount) || 0 };
        if (rlChallenge) {
          a.rate_limit_mode = 'challenge';
          a.rate_limit_challenge_mode = mode;
          a.rate_limit_challenge_ttl = Number(ttl) || 900;
        } else {
          a.rate_limit_mode = 'block';
          a.block_time = Number(blockTime) || 0;
        }
        return a;
      }
      default:
        return { type: actionType };
    }
  }

  // Inline error for the redirect URL (host-level rule); empty while the field is blank.
  const redirectHostLevel = useMemo(() => urlHostLevel(url), [url]);
  const redirectUrlError = (() => {
    if (actionType !== 'block_redirect') return '';
    if (!url.trim()) return submitted ? 'Redirect URL is required.' : '';
    if (redirectHostLevel === 0) return 'Enter a valid URL, e.g. https://example.com/blocked.';
    if (redirectHostLevel < REDIRECT_HOST_MIN || redirectHostLevel > REDIRECT_HOST_MAX)
      return `URL host must have between ${REDIRECT_HOST_MIN} and ${REDIRECT_HOST_MAX} levels.`;
    return '';
  })();

  const actionValid = (() => {
    switch (actionType) {
      case 'block_redirect':
        return (
          !!url.trim() &&
          REDIRECT_CODES.includes(Number(code)) &&
          redirectHostLevel >= REDIRECT_HOST_MIN &&
          redirectHostLevel <= REDIRECT_HOST_MAX
        );
      case 'challenge': return !!mode && CHALLENGE_TTLS.includes(Number(ttl));
      case 'rate_limit': {
        if (Number(totalCount) <= 0) return false;
        if (rlChallenge) return !!mode && CHALLENGE_TTLS.includes(Number(ttl));
        const bt = Number(blockTime);
        return bt >= 10 && bt <= 86400;
      }
      default: return true;
    }
  })();

  const valid =
    !!name.trim() && rows.length >= 1 && rows.length <= 5 && rows.every(rowComplete) && actionValid;

  const create = useCreateFirewallRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule created' }); trackFeatureAction('waf', 'firewall_rule', 'create'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateFirewallRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule updated' }); trackFeatureAction('waf', 'firewall_rule', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  const submit = () => {
    setSubmitted(true);
    if (!valid) return;
    const input: FirewallRuleInput = {
      name: name.trim(),
      enable: rule ? rule.enable : true,
      action: buildAction(),
      conditions: buildConditions(),
    };
    if (isEdit && rule) update.mutate({ domain, id: rule.id, input });
    else create.mutate({ domain, input });
  };

  // Human-readable summary of the matching conditions (mirrors the design's hint).
  const summary = useMemo(() => {
    const parts = validRows.map((r) => {
      const f = fieldFor(r.field);
      const o = opFor(r);
      const isGeo = o?.type === 'geo';
      const v = isGeo
        ? r.values.map((x) => GEO_OPTIONS.find((g) => g.value === x)?.label ?? x).join(', ')
        : opIsList(o) || o?.options?.length ? r.values.join(', ') : r.value;
      return `${f?.label ?? r.field} ${o?.label ?? r.operator} "${v}"`;
    });
    if (!parts.length) return '';
    return parts.reduce((acc, part, idx) => (idx === 0 ? part : `${acc} ${validRows[idx - 1].logic || 'and'} ${part}`), '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const header = (
    <PageHeader
      icon={<ScrollText size={22} />}
      title={`${isEdit ? 'Edit' : 'Add'} firewall rule`}
      description="Select a matching condition and an action to take when this rule is triggered."
      actions={<Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={back}>Back</Button>}
    />
  );

  if (!domain) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No domain selected" actions={<button onClick={back}>Back</button>}>
          Open this form from the Firewall Rules list so a domain is selected.
        </Alert>
      </Container>
    );
  }
  if (schemaQ.isError) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Couldn't load the rule builder" actions={<button onClick={() => schemaQ.refetch()}>Retry</button>}>
          The WAF service couldn't return the firewall rule schema. Try again.
        </Alert>
      </Container>
    );
  }
  if (!seeded) {
    return <Container size="lg">{header}<Skeleton style={{ height: 460 }} /></Container>;
  }
  if (isEdit && !rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      {header}
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit' : 'New'} firewall rule</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Rule name">
              <Input value={name} placeholder="Rule name" disabled={pending} invalid={submitted && !name.trim()} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />
            </Field>

            <div>
              <p className={styles.sectionTitle}>Rule configurations</p>
              <div className={styles.condList}>
                {rows.map((r, i) => {
                  const f = fieldFor(r.field);
                  const op = opFor(r);
                  const ops = f?.operators ?? [];
                  const isGeo = op?.type === 'geo';
                  const isList = opIsList(op);
                  const hasOptions = isGeo || isList || !!op?.options?.length;
                  const selectOptions = isGeo ? GEO_OPTIONS : isList ? [] : (op?.options ?? []).map((o) => ({ value: o, label: o }));
                  return (
                    <div key={i} className={styles.condRow}>
                      <div className={styles.condRail}>
                        {i === 0 ? (
                          <span className={styles.ifChip}>IF</span>
                        ) : (
                          <div className={styles.logicToggle}>
                            <button type="button" className={rows[i - 1].logic !== 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'and')}>And</button>
                            <button type="button" className={rows[i - 1].logic === 'or' ? styles.logicOn : styles.logicOff} disabled={pending} onClick={() => setLogic(i, 'or')}>Or</button>
                          </div>
                        )}
                      </div>

                      <div className={styles.condBody}>
                        <div className={styles.condTop}>
                          <Select value={r.field} onValueChange={(v) => changeField(i, v)} disabled={pending}>
                            <SelectTrigger aria-label="Field" className={styles.condField}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {fields.map((fl) => <SelectItem key={fl.value} value={fl.value}>{fl.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {f?.input_value && (
                            <Input value={r.key} placeholder="Filter value" disabled={pending} invalid={submitted && keyMissing(r)} onChange={(e) => patchRow(i, { key: e.target.value })} aria-label="Filter value" className={styles.condFilter} />
                          )}
                          <IconButton variant="ghost" size="sm" aria-label="Remove condition" disabled={pending || rows.length <= 1} onClick={() => removeRow(i)}>
                            <Trash2 size={15} />
                          </IconButton>
                        </div>
                        {f?.description ? <span className={styles.condHint}>{f.description}</span> : null}
                        <div className={styles.condBottom}>
                          <Select value={r.operator} onValueChange={(v) => changeOperator(i, v)} disabled={pending || !ops.length}>
                            <SelectTrigger aria-label="Operator" className={styles.condOp}><SelectValue placeholder="Operator" /></SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {op?.input_value === false ? null : hasOptions ? (
                            <div className={styles.condValue}>
                              <MultiSelect
                                placeholder={isGeo ? 'Select countries…' : isList ? 'Add values…' : 'Value'}
                                options={selectOptions}
                                value={r.values}
                                disabled={pending}
                                invalid={submitted && valueMissing(r)}
                                creatable={isList}
                                onChange={(v) => patchRow(i, { values: v })}
                              />
                            </div>
                          ) : (
                            <Input value={r.value} placeholder={fieldIsIp(f) ? '192.0.2.10' : 'Value'} disabled={pending} invalid={submitted && (valueMissing(r) || valueBadFormat(r))} onChange={(e) => patchRow(i, { value: e.target.value })} aria-label="Value" className={styles.condValue} />
                          )}
                        </div>
                        {submitted && valueBadFormat(r) ? (
                          <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-xs, 12px)' }}>
                            Enter a valid IPv4 address (e.g. 192.0.2.10 or 192.0.2.0/24).
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.addRow}>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('and')}>And</Button>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} disabled={pending || rows.length >= 5} onClick={() => addRow('or')}>Or</Button>
                {rows.length >= 5 ? <span className={styles.subHint}>Up to 5 conditions.</span> : null}
              </div>

              {summary ? <div className={styles.summary}>{summary}</div> : null}
            </div>

            <hr className={styles.divider} />
            <p className={styles.sectionTitle}>Then take an action…</p>
            <div className={styles.segment}>
              {actions.map((a) => (
                <Button key={a.type} size="sm" variant={actionType === a.type ? undefined : 'secondary'} disabled={pending} onClick={() => setActionType(a.type)}>
                  {a.label}
                </Button>
              ))}
            </div>

            {actionType === 'rate_limit' && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                <Field label="Number of queries" hint="The number of queries per minute is counted per Origin Shield instance. Each instance counts independently.">
                  <div className={styles.suffixWrap}>
                    <Input type="number" min={1} className={styles.noSpin} value={totalCount} placeholder="100" disabled={pending} invalid={submitted && Number(totalCount) <= 0} onChange={(e) => setTotalCount(e.target.value)} />
                    <span className={styles.suffix}>/ min.</span>
                  </div>
                </Field>
                <div className={styles.segment}>
                  <Button size="sm" variant={!rlChallenge ? undefined : 'secondary'} disabled={pending} onClick={() => setRlChallenge(false)}>Block</Button>
                  <Button size="sm" variant={rlChallenge ? undefined : 'secondary'} disabled={pending} onClick={() => setRlChallenge(true)}>Challenge</Button>
                </div>
                <Field label="Block time" hint="Issuing an HTTP 429 error when the threshold is exceeded.">
                  <div className={styles.suffixWrap}>
                    <Input type="number" min={10} className={styles.noSpin} value={blockTime} placeholder="60" disabled={pending} invalid={submitted && !rlChallenge && (Number(blockTime) < 10 || Number(blockTime) > 86400)} onChange={(e) => setBlockTime(e.target.value)} />
                    <span className={styles.suffix}>Sec.</span>
                  </div>
                </Field>
                {rlChallenge && <ChallengeFields mode={mode} setMode={setMode} ttl={ttl} setTtl={setTtl} pending={pending} />}
              </div>
            )}

            {actionType === 'block_redirect' && (
              <div className={styles.grid2}>
                <Field label="Status code">
                  <Select value={code} onValueChange={setCode} disabled={pending}>
                    <SelectTrigger aria-label="Status code"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REDIRECT_CODES.map((c) => <SelectItem key={c} value={String(c)}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Redirect URL" hint={`The URL host must have between ${REDIRECT_HOST_MIN} and ${REDIRECT_HOST_MAX} levels.`}>
                  <Input value={url} placeholder="https://example.com/blocked" disabled={pending} invalid={!!redirectUrlError} onChange={(e) => setUrl(e.target.value)} />
                  {redirectUrlError ? (
                    <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>{redirectUrlError}</span>
                  ) : null}
                </Field>
              </div>
            )}

            {actionType === 'challenge' && (
              <div className={styles.grid2}>
                <ChallengeFields mode={mode} setMode={setMode} ttl={ttl} setTtl={setTtl} pending={pending} />
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end', marginTop: 'var(--vn-space-2)' }}>
              {submitted && !valid ? (
                <span style={{ marginRight: 'auto', color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>
                  Please complete the highlighted fields.
                </span>
              ) : null}
              <Button variant="secondary" onClick={back}>Cancel</Button>
              <Button onClick={submit} disabled={pending}>{isEdit ? 'Save changes' : 'Create'}</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

/** Challenge mode + passage TTL selects (shared by Challenge and rate-limit-challenge). */
function ChallengeFields({ mode, setMode, ttl, setTtl, pending }: { mode: string; setMode: (v: string) => void; ttl: string; setTtl: (v: string) => void; pending: boolean }) {
  return (
    <>
      <Field label="Challenge mode">
        <Select value={mode} onValueChange={setMode} disabled={pending}>
          <SelectTrigger aria-label="Challenge mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHALLENGE_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Passage time">
        <Select value={ttl} onValueChange={setTtl} disabled={pending}>
          <SelectTrigger aria-label="Passage time"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHALLENGE_TTLS.map((t) => <SelectItem key={t} value={String(t)}>{fmtTtl(t)}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </>
  );
}

export default WafFirewallRuleForm;
